import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PostCard } from "@/components/workspace/PostCard";
import { EventCard } from "@/components/workspace/EventCard";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { Loader2, PlusCircle } from "lucide-react";
import { toast } from "sonner";

function CreateEventDialog({ onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    event_type: "masterclass",
    date_time: "",
    location: "",
    link: "",
  });

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, date_time: new Date(form.date_time).toISOString() };
      await client.post("/community/events", payload);
      toast.success(t("workspace.eventCreated"));
      setOpen(false);
      setForm({ title: "", description: "", event_type: "masterclass", date_time: "", location: "", link: "" });
      onCreated();
    } catch (e) {
      toast.error(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white gap-1.5" data-testid="community-create-event-button">
          <PlusCircle className="h-4 w-4" /> {t("workspace.createEvent")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("workspace.createEvent")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("workspace.eventTitle")}</Label>
            <Input className="mt-1.5 rounded-xl" value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="event-title-input" />
          </div>
          <div>
            <Label>{t("workspace.eventDescription")}</Label>
            <Textarea className="mt-1.5 rounded-xl" value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="event-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("workspace.eventType")}</Label>
              <Select value={form.event_type} onValueChange={(v) => update("event_type", v)}>
                <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="masterclass">{t("workspace.masterclass")}</SelectItem>
                  <SelectItem value="networking">{t("workspace.networking")}</SelectItem>
                  <SelectItem value="workshop">{t("workspace.workshop")}</SelectItem>
                  <SelectItem value="pitch_day">{t("workspace.pitch_day")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("workspace.eventDateTime")}</Label>
              <Input type="datetime-local" className="mt-1.5 rounded-xl" value={form.date_time} onChange={(e) => update("date_time", e.target.value)} data-testid="event-datetime-input" />
            </div>
          </div>
          <div>
            <Label>{t("workspace.eventLocation")}</Label>
            <Input className="mt-1.5 rounded-xl" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Online / Riyadh" />
          </div>
        </div>
        <DialogFooter>
          <Button
            className="w-full rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
            onClick={handleSubmit}
            disabled={submitting || !form.title || !form.date_time}
            data-testid="event-submit-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("workspace.createEvent")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Workspace() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const { content } = useSiteContent();
  const isAr = i18n.language === "ar";
  const pageContent = content?.pages?.workspace || {};
  const pageTitle = (isAr ? pageContent.title_ar : pageContent.title_en) || t("workspace.title");
  const pageSubtitle = (isAr ? pageContent.subtitle_ar : pageContent.subtitle_en) || t("workspace.subtitle");
  const [posts, setPosts] = useState(null);
  const [events, setEvents] = useState(null);
  const [postContent, setPostContent] = useState("");
  const [posting, setPosting] = useState(false);

  const loadPosts = useCallback(
    () => client.get("/community/posts").then((res) => setPosts(res.data.items)),
    [],
  );
  const loadEvents = useCallback(
    () => client.get("/community/events").then((res) => setEvents(res.data.items)),
    [],
  );

  useEffect(() => {
    loadPosts();
    loadEvents();
  }, [loadPosts, loadEvents]);

  const handlePost = async () => {
    if (!isAuthenticated) {
      loginWithGoogle("/onboarding");
      return;
    }
    if (!postContent.trim()) return;
    setPosting(true);
    try {
      await client.post("/community/posts", { content: postContent, post_type: "update" });
      setPostContent("");
      toast.success(t("workspace.postCreated"));
      loadPosts();
    } catch (e) {
      toast.error(t("common.error"));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12" data-testid="workspace-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">{pageTitle}</h1>
        <p className="text-muted-foreground mt-2">{pageSubtitle}</p>
      </div>

      <Tabs defaultValue="feed">
        <TabsList className="rounded-xl mb-6">
          <TabsTrigger value="feed" className="rounded-lg" data-testid="workspace-tab-feed">{t("workspace.tabFeed")}</TabsTrigger>
          <TabsTrigger value="events" className="rounded-lg" data-testid="workspace-tab-events">{t("workspace.tabEvents")}</TabsTrigger>
        </TabsList>

        <TabsContent value="feed" className="space-y-4">
          <Card className="rounded-2xl border border-border p-4">
            <Textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder={t("workspace.composerPlaceholder")}
              className="rounded-xl border-none focus-visible:ring-0 resize-none"
              data-testid="community-post-textarea"
            />
            <div className="flex justify-end mt-2">
              <Button
                size="sm"
                className="rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
                onClick={handlePost}
                disabled={posting}
                data-testid="community-create-post-button"
              >
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.post")}
              </Button>
            </div>
          </Card>

          {posts === null ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)
          ) : posts.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10">{t("workspace.noPosts")}</p>
          ) : (
            posts.map((p) => <PostCard key={p.post_id} post={p} />)
          )}
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-end">
            <CreateEventDialog onCreated={loadEvents} />
          </div>
          {events === null ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)
          ) : events.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10">{t("workspace.noEvents")}</p>
          ) : (
            events.map((e) => <EventCard key={e.event_id} event={e} />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
